import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Trash2, AlertTriangle, Info, Calendar, Repeat, CalendarRange } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TimePicker } from '@/components/ui/time-picker';
import { Label } from '@/components/ui/label';
import { DayCheckboxGroup } from './DayCheckboxGroup';
import { RecurrenceSelector, type RecurrenceType } from './RecurrenceSelector';
import { EndDateSelector, type EndType } from './EndDateSelector';
import type { ProviderAvailability } from '@/services/api/availability.service';

export interface ServiceOption {
  id: number;
  name: string;
  active?: boolean;
}

export interface UpdateFutureData {
  day_of_week: number;
  start_time: string;
  end_time: string;
  recurrence_type: RecurrenceType;
  recurrence_interval: number;
  monthly_week: number | null;
  monthly_day: number | null;
  end_type: EndType;
  end_date: string | null;
  occurrences: number | null;
  service_ids?: number[];
}

export interface CreateMultiDayData {
  days_of_week: number[];
  start_time: string;
  end_time: string;
  recurrence_type: RecurrenceType;
  recurrence_interval: number;
  monthly_week: number | null;
  monthly_day: number | null;
  end_type: EndType;
  end_date: string | null;
  occurrences: number | null;
  service_ids?: number[];
}

export interface UpdateGroupData {
  start_time: string;
  end_time: string;
  recurrence_type?: RecurrenceType;
  recurrence_interval?: number;
  end_type?: EndType;
  end_date?: string | null;
  occurrences?: number | null;
  service_ids?: number[];
  days_of_week?: number[];
}

interface EditAvailabilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availability: ProviderAvailability | null;
  allAvailabilities?: ProviderAvailability[]; // All availabilities to find series members
  services?: ServiceOption[];
  selectedDate?: string; // The specific calendar date (yyyy-MM-dd) the user clicked on
  onUpdateFuture: (id: number, data: UpdateFutureData) => Promise<void>;
  onCreateMultiDay?: (data: CreateMultiDayData) => Promise<void>;
  onUpdateGroup?: (groupId: string, data: UpdateGroupData) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onDeleteGroup?: (groupId: string, fromDate?: string) => Promise<void>;
  onDeleteSingleDay?: (date: string, recurrenceGroupId?: string, startTime?: string, endTime?: string) => Promise<void>; // Mark a single day as unavailable (for recurring series)
}

const DAY_FULL_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const WEEK_OPTION_KEYS: Record<number, string> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  '-1': 'last',
};

export function EditAvailabilityDialog({
  open,
  onOpenChange,
  availability,
  allAvailabilities = [],
  services = [],
  selectedDate,
  onUpdateFuture,
  onCreateMultiDay,
  onUpdateGroup,
  onDelete,
  onDeleteGroup,
  onDeleteSingleDay,
}: EditAvailabilityDialogProps) {
  const { t } = useTranslation('staff');

  // Helper functions for translations
  const getDayFullName = (dayIndex: number) => t(`availability.days.${DAY_FULL_KEYS[dayIndex]}`);
  const getWeekName = (week: number) => t(`availability.recurrence.weekOptions.${WEEK_OPTION_KEYS[week]}`);

  // Form state
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>('weekly');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [monthlyWeek, setMonthlyWeek] = useState<number>(1);
  const [monthlyDay, setMonthlyDay] = useState<number>(1);
  const [endType, setEndType] = useState<EndType>('never');
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [occurrences, setOccurrences] = useState<number>(10);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteChoiceDialog, setShowDeleteChoiceDialog] = useState(false);
  const [deleteScope, setDeleteScope] = useState<'single' | 'future' | 'series'>('single');
  const [showSeriesChoiceDialog, setShowSeriesChoiceDialog] = useState(false);

  // Track which availability we've initialized to prevent re-initialization on re-renders
  const initializedIdRef = useRef<number | null>(null);

  // Initialize form from availability - only when opening dialog or editing different availability
  useEffect(() => {
    if (open && availability && availability.id !== initializedIdRef.current) {
      // If part of a series, select ALL days in the series; otherwise just the clicked day
      let daysToSelect: number[];
      if (availability.recurrence_group_id && allAvailabilities.length > 0) {
        daysToSelect = allAvailabilities
          .filter(a => a.recurrence_group_id === availability.recurrence_group_id)
          .map(a => a.day_of_week)
          .sort((a, b) => a - b);
      } else {
        daysToSelect = [availability.day_of_week];
      }
      setSelectedDays(daysToSelect);
      setRecurrenceType((availability.recurrence_type as RecurrenceType) || 'weekly');
      setRecurrenceInterval(availability.recurrence_interval || 1);
      setMonthlyWeek(availability.monthly_week || 1);
      setMonthlyDay(availability.monthly_day || availability.day_of_week);
      setEndType((availability.end_type as EndType) || 'never');
      setEndDate(availability.end_date ? new Date(availability.end_date) : null);
      setOccurrences(availability.occurrences || 10);
      setStartTime(availability.start_time || '09:00');
      setEndTime(availability.end_time || '17:00');
      setSelectedServiceIds(availability.services?.map(s => s.id) || availability.service_ids || []);
      initializedIdRef.current = availability.id;
    }
  }, [open, availability, allAvailabilities]);

  // Reset all form + UI state when dialog closes so next open starts clean.
  useEffect(() => {
    if (!open) {
      initializedIdRef.current = null;
      setSelectedDays([]);
      setRecurrenceType('weekly');
      setRecurrenceInterval(1);
      setMonthlyWeek(1);
      setMonthlyDay(1);
      setEndType('never');
      setEndDate(null);
      setOccurrences(10);
      setStartTime('09:00');
      setEndTime('17:00');
      setSelectedServiceIds([]);
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
      setShowDeleteChoiceDialog(false);
      setDeleteScope('single');
      setShowSeriesChoiceDialog(false);
    }
  }, [open]);

  const hasRecurrenceGroup = availability?.recurrence_group_id != null;

  // Generate summary text - must be called before any early returns
  const summary = useMemo(() => {
    const timeRange = `${startTime} - ${endTime}`;

    if (recurrenceType === 'monthly') {
      const weekName = getWeekName(monthlyWeek);
      const dayName = getDayFullName(monthlyDay);
      let text = t('availability.summary.monthly', { week: weekName, day: dayName, timeRange });
      if (endType === 'on_date' && endDate) {
        text += t('availability.summary.until', { date: format(endDate, 'MMM d, yyyy') });
      } else if (endType === 'after_occurrences' && occurrences) {
        text += t('availability.summary.times', { count: occurrences });
      }
      return text;
    }

    if (selectedDays.length === 0) {
      return t('availability.summary.selectDays');
    }

    const dayNames = selectedDays.map(d => getDayFullName(d));
    let text = '';

    if (recurrenceInterval === 1) {
      if (selectedDays.length === 7) {
        text = t('availability.summary.everyDay', { timeRange });
      } else if (selectedDays.length === 5 &&
                 [1, 2, 3, 4, 5].every(d => selectedDays.includes(d))) {
        text = t('availability.summary.everyWeekday', { timeRange });
      } else {
        text = t('availability.summary.everyDays', { days: dayNames.join(', '), timeRange });
      }
    } else {
      text = t('availability.summary.everyNWeeks', { count: recurrenceInterval, days: dayNames.join(', '), timeRange });
    }

    if (endType === 'on_date' && endDate) {
      text += t('availability.summary.until', { date: format(endDate, 'MMM d, yyyy') });
    } else if (endType === 'after_occurrences' && occurrences) {
      text += t('availability.summary.times', { count: occurrences });
    }

    return text;
  }, [
    selectedDays, recurrenceType, recurrenceInterval,
    monthlyWeek, monthlyDay, endType, endDate, occurrences,
    startTime, endTime, t, getDayFullName, getWeekName
  ]);

  // Validation - must be called before any early returns
  const isValid = useMemo(() => {
    if (startTime >= endTime) return false;
    if (services.length > 0 && selectedServiceIds.length === 0) return false;
    if (recurrenceType === 'monthly') {
      return monthlyWeek !== null && monthlyDay !== null;
    }
    return selectedDays.length > 0;
  }, [
    selectedDays, recurrenceType, monthlyWeek, monthlyDay,
    startTime, endTime, services.length, selectedServiceIds.length
  ]);

  // Early return AFTER all hooks
  if (!availability) return null;

  // Build common update data
  const getCommonData = () => ({
    start_time: startTime,
    end_time: endTime,
    recurrence_type: recurrenceType,
    recurrence_interval: recurrenceInterval,
    monthly_week: recurrenceType === 'monthly' ? monthlyWeek : null,
    monthly_day: recurrenceType === 'monthly' ? monthlyDay : null,
    end_type: endType,
    end_date: endType === 'on_date' && endDate ? format(endDate, 'yyyy-MM-dd') : null,
    occurrences: endType === 'after_occurrences' ? occurrences : null,
    service_ids: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
  });

  // Handle save button click - check if we need to show series choice dialog
  const handleSaveClick = () => {
    if (isSubmitting || !isValid) return;

    // If this availability is part of a recurrence group and onUpdateGroup is available,
    // show the series choice dialog (regardless of how many days are selected)
    if (hasRecurrenceGroup && onUpdateGroup) {
      setShowSeriesChoiceDialog(true);
    } else {
      // No group - just update normally
      handleUpdate('single');
    }
  };

  // Handle the series choice
  const handleSeriesChoice = async (scope: 'single' | 'future' | 'series') => {
    setShowSeriesChoiceDialog(false);
    await handleUpdate(scope);
  };

  // Perform the actual update
  const handleUpdate = async (scope: 'single' | 'future' | 'series' = 'single') => {
    if (isSubmitting || !isValid) return;
    setIsSubmitting(true);
    try {
      const commonData = getCommonData();

      // If updating entire series (all days)
      if (scope === 'series' && hasRecurrenceGroup && onUpdateGroup) {
        await onUpdateGroup(availability.recurrence_group_id!, {
          start_time: startTime,
          end_time: endTime,
          recurrence_type: recurrenceType,
          recurrence_interval: recurrenceInterval,
          end_type: endType,
          end_date: endType === 'on_date' && endDate ? format(endDate, 'yyyy-MM-dd') : null,
          occurrences: endType === 'after_occurrences' ? occurrences : null,
          service_ids: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
          days_of_week: selectedDays,
        });
        onOpenChange(false);
        return;
      }

      // "From this day forward" - update this day and all following in series, from today forward
      // Allows adding/removing days, uses update_group with days_of_week
      if (scope === 'future' && hasRecurrenceGroup && onUpdateGroup) {
        await onUpdateGroup(availability.recurrence_group_id!, {
          start_time: startTime,
          end_time: endTime,
          recurrence_type: recurrenceType,
          recurrence_interval: recurrenceInterval,
          end_type: endType,
          end_date: endType === 'on_date' && endDate ? format(endDate, 'yyyy-MM-dd') : null,
          occurrences: endType === 'after_occurrences' ? occurrences : null,
          service_ids: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
          days_of_week: selectedDays,
        });
        onOpenChange(false);
        return;
      }

      // Single day update logic - when scope is 'single', only update this specific availability
      // Use the availability's original day_of_week to avoid creating duplicates
      if (scope === 'single' && hasRecurrenceGroup) {
        // For series with "This day only" - update just this availability with its original day
        await onUpdateFuture(availability.id, {
          ...commonData,
          day_of_week: availability.day_of_week,
        });
        onOpenChange(false);
        return;
      }

      // Non-series availability logic
      if (recurrenceType === 'monthly') {
        // Monthly uses monthlyDay, just update existing
        await onUpdateFuture(availability.id, {
          ...commonData,
          day_of_week: monthlyDay,
        });
      } else if (selectedDays.length === 1) {
        // Single day selected, just update existing
        await onUpdateFuture(availability.id, {
          ...commonData,
          day_of_week: selectedDays[0],
        });
      } else {
        // Multiple days selected (non-series availability)
        const originalDay = availability.day_of_week;
        const hasOriginalDay = selectedDays.includes(originalDay);
        const additionalDays = selectedDays.filter(d => d !== originalDay);

        if (hasOriginalDay && additionalDays.length > 0) {
          // Original day still selected + adding more days
          // Check if settings changed for the original day
          const settingsChanged =
            startTime !== availability.start_time ||
            endTime !== availability.end_time ||
            recurrenceType !== availability.recurrence_type ||
            recurrenceInterval !== availability.recurrence_interval ||
            endType !== availability.end_type;

          if (settingsChanged) {
            // Settings changed - update existing
            await onUpdateFuture(availability.id, {
              ...commonData,
              day_of_week: originalDay,
            });
          }
          // Create new availabilities for additional days only
          if (onCreateMultiDay) {
            await onCreateMultiDay({
              ...commonData,
              days_of_week: additionalDays,
            });
          }
        } else if (hasOriginalDay) {
          // Only original day selected (shouldn't reach here with length > 1, but safety)
          await onUpdateFuture(availability.id, {
            ...commonData,
            day_of_week: originalDay,
          });
        } else {
          // Original day not selected - update existing with first new day, create for rest
          await onUpdateFuture(availability.id, {
            ...commonData,
            day_of_week: selectedDays[0],
          });

          // Create new availabilities for remaining days
          const remainingDays = selectedDays.slice(1);
          if (remainingDays.length > 0 && onCreateMultiDay) {
            await onCreateMultiDay({
              ...commonData,
              days_of_week: remainingDays,
            });
          }
        }
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update availability:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete button click - show choice dialog if series, otherwise show confirm
  const handleDeleteClick = () => {
    if (hasRecurrenceGroup && onDeleteGroup) {
      setShowDeleteChoiceDialog(true);
    } else {
      setDeleteScope('single');
      setShowDeleteConfirm(true);
    }
  };

  // Handle delete choice selection
  const handleDeleteChoice = (scope: 'single' | 'future' | 'series') => {
    setDeleteScope(scope);
    setShowDeleteChoiceDialog(false);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (deleteScope === 'series' && hasRecurrenceGroup && onDeleteGroup) {
        // Delete entire series
        await onDeleteGroup(availability.recurrence_group_id!);
      } else if (deleteScope === 'future' && hasRecurrenceGroup && onDeleteGroup) {
        // End series from the selected date forward
        await onDeleteGroup(availability.recurrence_group_id!, selectedDate);
      } else if (deleteScope === 'single' && hasRecurrenceGroup) {
        // Single day from recurring series - mark as unavailable override (scoped to this group)
        if (onDeleteSingleDay && selectedDate) {
          await onDeleteSingleDay(selectedDate, availability.recurrence_group_id || undefined, availability.start_time || undefined, availability.end_time || undefined);
        } else {
          console.error('Cannot delete single day: missing selectedDate or onDeleteSingleDay handler');
          return;
        }
      } else {
        // Non-recurring one-off availability - hard delete
        await onDelete(availability.id);
      }
      setShowDeleteConfirm(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to delete availability:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('availability.edit.title', 'Edit Availability')}</DialogTitle>
            <DialogDescription>
              {t('availability.edit.description', 'Modify your recurring availability schedule')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Info banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="text-amber-800">
                  {t('availability.edit.futureOnlyShort', 'Changes apply from today forward')}
                </span>
              </div>
            </div>

            {/* Recurrence Type */}
            <div className="space-y-3">
              <Label variant="section">
                {t('availability.recurrence.repeats', 'Repeats')}
              </Label>
              <RecurrenceSelector
                recurrenceType={recurrenceType}
                onRecurrenceTypeChange={setRecurrenceType}
                recurrenceInterval={recurrenceInterval}
                onRecurrenceIntervalChange={setRecurrenceInterval}
                monthlyWeek={monthlyWeek}
                onMonthlyWeekChange={setMonthlyWeek}
                monthlyDay={monthlyDay}
                onMonthlyDayChange={setMonthlyDay}
              />
            </div>

            {/* Day selection (not for monthly) */}
            {recurrenceType !== 'monthly' && (
              <div className="space-y-3">
                <Label variant="section">
                  {t('availability.dialog.days', 'Days')}
                </Label>
                <DayCheckboxGroup
                  value={selectedDays}
                  onChange={setSelectedDays}
                  showPresets={false}
                  showSummary={false}
                />
              </div>
            )}

            {/* End date settings */}
            <div className="space-y-3">
              <Label variant="section">
                {t('availability.ends.label', 'Ends')}
              </Label>
              <EndDateSelector
                endType={endType}
                onEndTypeChange={setEndType}
                endDate={endDate}
                onEndDateChange={setEndDate}
                occurrences={occurrences}
                onOccurrencesChange={setOccurrences}
              />
            </div>

            <hr className="border-gray-200" />

            {/* Time selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('availability.dialog.startTime', 'Start time')}</Label>
                <TimePicker
                  value={startTime}
                  onChange={setStartTime}
                  placeholder={t('availability.dialog.selectStartTime', 'Select start time')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('availability.dialog.endTime', 'End time')}</Label>
                <TimePicker
                  value={endTime}
                  onChange={setEndTime}
                  placeholder={t('availability.dialog.selectEndTime', 'Select end time')}
                />
              </div>
            </div>

            {/* Time validation error */}
            {startTime && endTime && startTime >= endTime && (
              <p className="text-sm text-red-500">
                {t('availability.edit.timeError', 'End time must be after start time')}
              </p>
            )}

            {/* Service selection - no nested scroll */}
            {services.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label variant="section">
                    {t('availability.dialog.services', 'Services')}
                  </Label>
                  {selectedServiceIds.length > 0 && (
                    <span className="text-xs text-blue-600 font-medium">
                      {t('availability.dialog.selectedServices', '{{count}} selected', { count: selectedServiceIds.length })}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {services.map((service) => (
                    <label
                      key={service.id}
                      htmlFor={`edit-service-${service.id}`}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors min-h-[44px]",
                        selectedServiceIds.includes(service.id)
                          ? "bg-blue-50 border-blue-300"
                          : "bg-white border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <Checkbox
                        id={`edit-service-${service.id}`}
                        checked={selectedServiceIds.includes(service.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedServiceIds([...selectedServiceIds, service.id]);
                          } else {
                            setSelectedServiceIds(selectedServiceIds.filter(id => id !== service.id));
                          }
                        }}
                      />
                      <span className="text-sm">
                        {service.name}
                        {service.active === false && (
                          <span className="ml-1 text-xs text-gray-400">{t('availability.dialog.inactive', '(inactive)')}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-900">{t('availability.dialog.preview', 'Preview')}</p>
                  <p className="text-sm text-blue-700 mt-1">{summary}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer - separated primary and destructive actions */}
          <div className="space-y-3 pt-2 border-t">
            {/* Primary actions */}
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full sm:flex-1"
              >
                {t('availability.edit.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleSaveClick}
                disabled={!isValid || isSubmitting}
                className="w-full sm:flex-1"
              >
                {isSubmitting ? t('availability.edit.saving', 'Saving...') : t('availability.edit.save', 'Save Changes')}
              </Button>
            </DialogFooter>

            {/* Destructive action - separated and less prominent */}
            <Button
              variant="ghost"
              onClick={handleDeleteClick}
              disabled={isSubmitting}
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('availability.edit.delete', 'Delete Availability')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              {t('availability.edit.deleteTitle', 'Delete Availability?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteScope === 'series'
                ? t('availability.edit.deleteSeriesDescription', 'This will remove all availability blocks in this series. This action cannot be undone.')
                : deleteScope === 'future'
                  ? t('availability.edit.deleteFutureDescription', 'This will remove this day and all future occurrences. This action cannot be undone.')
                  : t('availability.edit.deleteDescription', 'This will remove this availability block. This action cannot be undone.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              {t('availability.edit.cancelDelete', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmitting
                ? t('availability.edit.deleting', 'Deleting...')
                : deleteScope === 'series'
                  ? t('availability.edit.deleteSeriesButton', 'Delete Series')
                  : deleteScope === 'future'
                    ? t('availability.edit.deleteFutureButton', 'Delete Future')
                    : t('availability.edit.deleteButton', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete choice dialog (Google Calendar-style) */}
      <Dialog open={showDeleteChoiceDialog} onOpenChange={setShowDeleteChoiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              {t('availability.deleteSeries.title', 'Delete recurring availability')}
            </DialogTitle>
            <DialogDescription>
              {t('availability.deleteSeries.description', 'This availability is part of a recurring series. What would you like to delete?')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button
              variant="outline"
              onClick={() => handleDeleteChoice('single')}
              disabled={isSubmitting}
              className="justify-start h-auto py-3 px-4"
            >
              <Calendar className="mr-3 h-5 w-5 text-gray-500" />
              <div className="text-left">
                <div className="font-medium">
                  {t('availability.deleteSeries.thisOnly', 'This day only')}
                </div>
                <div className="text-sm text-gray-500 font-normal">
                  {t('availability.deleteSeries.thisOnlyDescription', 'Only delete this specific availability')}
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDeleteChoice('future')}
              disabled={isSubmitting}
              className="justify-start h-auto py-3 px-4"
            >
              <CalendarRange className="mr-3 h-5 w-5 text-gray-500" />
              <div className="text-left">
                <div className="font-medium">
                  {t('availability.deleteSeries.fromThisDay', 'From this day forward')}
                </div>
                <div className="text-sm text-gray-500 font-normal">
                  {t('availability.deleteSeries.fromThisDayDescription', 'Delete this and all future occurrences')}
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDeleteChoice('series')}
              disabled={isSubmitting}
              className="justify-start h-auto py-3 px-4"
            >
              <Repeat className="mr-3 h-5 w-5 text-gray-500" />
              <div className="text-left">
                <div className="font-medium">
                  {t('availability.deleteSeries.allSeries', 'All days in this series')}
                </div>
                <div className="text-sm text-gray-500 font-normal">
                  {t('availability.deleteSeries.allSeriesDescription', 'Delete all availabilities created together')}
                </div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowDeleteChoiceDialog(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Series edit choice dialog (Google Calendar-style) */}
      <Dialog open={showSeriesChoiceDialog} onOpenChange={setShowSeriesChoiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('availability.editSeries.title', 'Edit recurring availability')}
            </DialogTitle>
            <DialogDescription>
              {t('availability.editSeries.description', 'This availability is part of a recurring series. How would you like to apply your changes?')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            {/* Only show "This day only" if weekdays haven't changed */}
            {(() => {
              const originalDays = allAvailabilities
                .filter(a => a.recurrence_group_id === availability?.recurrence_group_id)
                .map(a => a.day_of_week)
                .sort((a, b) => a - b);
              const currentDays = [...selectedDays].sort((a, b) => a - b);
              const daysChanged = JSON.stringify(originalDays) !== JSON.stringify(currentDays);

              if (daysChanged) return null;

              return (
                <Button
                  variant="outline"
                  onClick={() => handleSeriesChoice('single')}
                  disabled={isSubmitting}
                  className="justify-start h-auto py-3 px-4"
                >
                  <Calendar className="mr-3 h-5 w-5 text-gray-500" />
                  <div className="text-left">
                    <div className="font-medium">
                      {t('availability.editSeries.thisOnly', 'This day only')}
                    </div>
                    <div className="text-sm text-gray-500 font-normal">
                      {t('availability.editSeries.thisOnlyDescription', 'Only update this specific availability')}
                    </div>
                  </div>
                </Button>
              );
            })()}
            <Button
              variant="outline"
              onClick={() => handleSeriesChoice('future')}
              disabled={isSubmitting}
              className="justify-start h-auto py-3 px-4"
            >
              <CalendarRange className="mr-3 h-5 w-5 text-gray-500" />
              <div className="text-left">
                <div className="font-medium">
                  {t('availability.editSeries.fromThisDay', 'From this day forward')}
                </div>
                <div className="text-sm text-gray-500 font-normal">
                  {t('availability.editSeries.fromThisDayDescription', 'Update this and all future occurrences')}
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSeriesChoice('series')}
              disabled={isSubmitting}
              className="justify-start h-auto py-3 px-4"
            >
              <Repeat className="mr-3 h-5 w-5 text-gray-500" />
              <div className="text-left">
                <div className="font-medium">
                  {t('availability.editSeries.allSeries', 'All days in this series')}
                </div>
                <div className="text-sm text-gray-500 font-normal">
                  {t('availability.editSeries.allSeriesDescription', 'Update all availabilities created together')}
                </div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowSeriesChoiceDialog(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
